import { Dialog, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { DialogProps } from '@mui/material'

/**
 * A dialog that becomes a full-screen sheet on a phone.
 *
 * Below `sm` a centred dialog leaves a strip of backdrop it cannot use and
 * puts its own scroll region inside the page's, which is how a surface ends
 * up with two or three stacked scrollbars. Taking the whole screen gives the
 * content the width it needs and leaves exactly one place to scroll.
 *
 * The explicit white `backgroundColor` is not redundant. MuiDrawer's theme
 * override paints paper with gray900 for the navigation rail, and a content
 * surface that inherits it renders unreadable dark-on-dark — the cause of
 * MWA-253's defects. Anything rendering its own paper states its own colour.
 */
export default function ResponsiveSheet({
  children,
  PaperProps,
  fullScreenBelow = 'sm',
  ...props
}: DialogProps & {
  /**
   * Where the dialog becomes a sheet. `sm` (600) suits a short dialog; a form
   * with several fields needs the whole screen sooner, so it passes 768.
   */
  fullScreenBelow?: number | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down(fullScreenBelow))

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      {...props}
      PaperProps={{
        ...PaperProps,
        sx: {
          backgroundColor: 'background.paper',
          color: 'text.primary',
          ...(fullScreen
            ? { borderRadius: 0 }
            : { borderRadius: '10px' }),
          ...(PaperProps?.sx || {}),
        },
      }}
    >
      {children}
    </Dialog>
  )
}
